// Per-day JOURNAL of the manual moves a board runner makes on the board: dragging a person onto a room,
// taking someone off a room, handing a room to a different anesthesiologist, or locking a person to a site.
// This is a PASSIVE record only — writing to it never changes the allocation. The point is to capture the
// runner's real-time decisions so the logic behind them can be studied and, down the road, learned.
//
// Stored locally per day (survives refresh), exactly like the other board annotations (site locks, room
// transfers, notes). Nothing is ever sent off the machine.

export type MoveKind = 'place' | 'remove' | 'transfer' | 'lock' | 'unlock';

export interface MoveLogEntry {
  ts: string;          // ISO timestamp of when the move was made (device clock)
  kind: MoveKind;
  who: string;         // the person moved (or, for a transfer, the anesthesiologist the room went TO)
  room?: string;       // room label involved (place / remove / transfer)
  slot?: string;       // 'anesthesiologist' | 'CRNA' | 'student' (place / remove)
  from?: string;       // prior occupant, source room, or the doctor a room was taken from
  to?: string;         // destination site (lock) or destination doctor (transfer)
  detail: string;      // human-readable one-line summary, precomputed at log time
}

export const moveLogKey = (date: string) => `oc-board-move-log-${date}`;

export function loadMoveLog(date: string): MoveLogEntry[] {
  try {
    const raw = localStorage.getItem(moveLogKey(date));
    if (raw) { const v = JSON.parse(raw); if (Array.isArray(v)) return v as MoveLogEntry[]; }
  } catch { /* ignore */ }
  return [];
}

// Append one move (timestamped now) and return the updated list. Oldest-first; the UI reverses for display.
export function appendMove(date: string, entry: Omit<MoveLogEntry, 'ts'>): MoveLogEntry[] {
  const log = loadMoveLog(date);
  const next = [...log, { ts: new Date().toISOString(), ...entry }];
  try { localStorage.setItem(moveLogKey(date), JSON.stringify(next)); } catch { /* ignore */ }
  return next;
}

// Wipe the journal for one day (local only — does not touch the allocation).
export function clearMoveLog(date: string): MoveLogEntry[] {
  try { localStorage.removeItem(moveLogKey(date)); } catch { /* ignore */ }
  return [];
}
